import React, { useMemo, useState } from "react";
import TextLink from "components/common/TextLink";
import TextLinkV2 from "components/common/TextLinkV2";
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image";

import {
  cameraDaysClass,
  cameraImageClass,
  cameraImageContainer,
  cameraInfoContainer,
  cameraPriceClass,
  cardContainer,
  circleButtonSelectedClass,
  circleButtonUnselectedClass,
  controllerQty,
  errorClass,
  licenseTypeButtonsClass,
  licenseTypeContainerClass,
  lineClass,
  qtyInput,
  resolutionText,
  smallFontClass,
  tooltipClass,
  tooltipContainer,
  tooltipIconClass,
  tooltipTextClass,
  typeButtonSelectedClass,
  typeButtonUnselectedClass,
  yearsButtonsClass,
  yearsContainerClass,
} from "../styles/pricingStyles";
import { FlexRowApart } from "components/rui/layout";

const MAX = 999;
const MIN = 0;

export default function RelayCard({ info }) {
  const {
    name,
    category,
    title,
    resolution,
    price,
    image,
    altText,
    storage,
    licenseOptions = [],
  } = info;

  const [selectedLicenseIndex, setSelectedLicenseIndex] = useState(0);
  const [selectedTermIndex, setSelectedTermIndex] = useState(0);
  const [cameraCount, setCameraCount] = useState(1);

  const selectedLicense = useMemo(() => {
    return licenseOptions[selectedLicenseIndex];
  }, [licenseOptions, selectedLicenseIndex]);

  const selectedTerm = useMemo(() => {
    return selectedLicense?.terms?.[selectedTermIndex];
  }, [selectedLicense, selectedTermIndex]);

  const licenseCosts = useMemo(() => {
    return (selectedTerm?.price ?? 0) * Math.max(cameraCount, 1);
  }, [selectedTerm, cameraCount]);

  const hardwareNeeded = useMemo(() => {
    return Math.max(Math.ceil(cameraCount / 10), 1);
  }, [cameraCount]);

  const total = useMemo(() => {
    const hardwareCosts = price * hardwareNeeded;
    return hardwareCosts + licenseCosts;
  }, [price, licenseCosts, cameraCount, hardwareNeeded]);

  return (
    <div className={cardContainer} id={name.toLowerCase()}>
      {/* Image Section */}
      <div className={cameraImageContainer}>
        <GatsbyImage
          image={getImage(image)}
          className={cameraImageClass}
          alt={altText ?? name}
          objectFit="contain"
        />
      </div>

      {/* Info Section */}
      <div className={cameraInfoContainer}>
        {/* Header */}
        <div>
          <FlexRowApart>
            <div>
              <h3 className="heavy">
                {name} {title}{" "}
                <span className={resolutionText}>{resolution}</span>
              </h3>
              <p className={smallFontClass}>price shown is per unit</p>
            </div>
            <h3>${price.toLocaleString()}</h3>
          </FlexRowApart>
          <TextLinkV2 path={info.link} target="_blank" title={"View Specs"} />
        </div>

        {/* Storage */}
        <div>
          <SectionWithTooltip
            title="Storage"
            tooltipText="Up to 10 cameras per N100 and up to 20 days of storage per camera."
          ></SectionWithTooltip>
          <div className={cameraDaysClass}>
            <button className={circleButtonSelectedClass}>{storage}</button>
          </div>
        </div>

        {/* Camera Quantity */}
        <div>
          <SectionWithTooltip
            title={"Camera Streams"}
            subTitle={"How many cameras do you plan on streaming?"}
            tooltipText={
              "Relay N100 supports up to 10 camera streams and is based on camera resolution, bitrate, and motion."
            }
          >
            <input
              className={qtyInput}
              type="number"
              value={cameraCount}
              min={MIN}
              max={MAX}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === "") {
                  setCameraCount("");
                  return;
                }
                const parsed = parseInt(raw, 10);
                if (!isNaN(parsed)) {
                  if (parsed <= MAX) {
                    setCameraCount(parsed);
                  } else {
                    setCameraCount(MAX);
                  }
                }
              }}
              onBlur={() => {
                if (cameraCount === "") {
                  setCameraCount(1);
                }
              }}
            />
          </SectionWithTooltip>
        </div>

        {/* Hardware Needed */}
        <div>
          <SectionWithTooltip
            title={`${category}s Needed`}
            subTitle={`${category}s needed to support this configuration`}
            tooltipText={
              "Relay N100 supports up to 10 camera streams per unit."
            }
          >
            <div className={controllerQty}>{hardwareNeeded}</div>
          </SectionWithTooltip>
        </div>

        {/* License */}
        <div className={licenseTypeContainerClass}>
          <SectionWithTooltip
            title="License"
            subTitle={"License is per camera"}
            tooltipText={
              <>
                To enable AI features on third-party cameras using the N100
                Relay, a Professional or Enterprise license is required per
                camera. See our{" "}
                <TextLink to="/license-comparison/">
                  License Comparison
                </TextLink>{" "}
                page.
              </>
            }
          >
            {!!licenseCosts && (
              <div>
                <div className={cameraPriceClass}>
                  ${selectedTerm?.price.toLocaleString()}
                </div>
                {cameraCount > 1 && (
                  <p className={smallFontClass}>
                    ${licenseCosts.toLocaleString()}
                  </p>
                )}
              </div>
            )}
          </SectionWithTooltip>

          <div className={licenseTypeButtonsClass} style={{ flexWrap: "wrap" }}>
            {licenseOptions.map((license, index) => (
              <button
                key={index}
                className={
                  index === selectedLicenseIndex
                    ? typeButtonSelectedClass
                    : typeButtonUnselectedClass
                }
                onClick={() => {
                  setSelectedLicenseIndex(index);
                  setSelectedTermIndex(0);
                }}
              >
                {license.name}
              </button>
            ))}
          </div>
        </div>

        {/* Years */}
        {selectedLicense?.terms && (
          <div className={yearsContainerClass} style={{ marginTop: "-2.5rem" }}>
            <h3>Years</h3>
            <div className={yearsButtonsClass}>
              {selectedLicense.terms.map((term, index) => (
                <button
                  key={index}
                  className={
                    index === selectedTermIndex
                      ? circleButtonSelectedClass
                      : circleButtonUnselectedClass
                  }
                  onClick={() => setSelectedTermIndex(index)}
                >
                  {term.term}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Total */}
        <div>
          <div className={lineClass}>
            <h3 className="heavy">Total Cost</h3>
            <h3>{`$${total.toLocaleString()}`}</h3>
          </div>
          <div className={lineClass}>
            <p className={smallFontClass}>Includes hardware and license</p>
            <p className={smallFontClass}>
              <strong>(MSRP USD)</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionWithTooltip({ title, subTitle, tooltipText, children }) {
  return (
    <div className={lineClass}>
      <div>
        <div className={tooltipContainer}>
          <h3>{title}</h3>
          {tooltipText && (
            <div className={tooltipClass}>
              <StaticImage
                src="../img/tooltip-icon.png"
                className={tooltipIconClass}
                alt={`${title} Tooltip`}
                objectFit="contain"
              />
              <span className={tooltipTextClass}>{tooltipText}</span>
            </div>
          )}
        </div>
        <p className={smallFontClass}>{subTitle}</p>
      </div>
      {children}
    </div>
  );
}
